import type { BlockRecord } from '../blocks/types';
import type { BlockPropertyRecord } from './types';
import { monthLabel, rowDateRange, shiftMonth } from './calendar-grid.js';
import { formatDateValue } from '@couplespace/core/databases';

export const TIMELINE_DAY_WIDTH_PX = 28;
export const TIMELINE_LABEL_WIDTH_PX = 200;

export type TimelineSpan = 'month' | 'quarter' | 'year';

export const TIMELINE_SPANS: { id: TimelineSpan; label: string }[] = [
  { id: 'month', label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'year', label: 'Year' },
];

export type TimelineDay = {
  iso: string;
  dayOfMonth: number;
  weekday: number;
  isWeekStart: boolean;
  isToday: boolean;
  inMonth: boolean;
};

export type TimelineBarPlacement = {
  row: BlockRecord;
  startIso: string;
  endIso: string;
  leftPx: number;
  widthPx: number;
  extendsBefore: boolean;
  extendsAfter: boolean;
};

function isoToUtcMs(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y!, m! - 1, d!);
}

function formatIsoFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function todayIsoDate(): string {
  return formatIsoFromDate(new Date());
}

/** Monday-first day strip covering the month plus partial weeks. */
export function buildTimelineDays(
  year: number,
  month: number,
  todayIso: string = todayIsoDate(),
): TimelineDay[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const padStart = (first.getDay() + 6) % 7;
  const totalInMonth = padStart + last.getDate();
  const padEnd = (7 - (totalInMonth % 7)) % 7;
  const count = totalInMonth + padEnd;

  const days: TimelineDay[] = [];
  const cursor = new Date(first);
  cursor.setDate(cursor.getDate() - padStart);

  for (let i = 0; i < count; i++) {
    const iso = formatIsoFromDate(cursor);
    days.push({
      iso,
      dayOfMonth: cursor.getDate(),
      weekday: (cursor.getDay() + 6) % 7,
      isWeekStart: ((cursor.getDay() + 6) % 7) === 0,
      isToday: iso === todayIso,
      inMonth: cursor.getMonth() === month,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function normalizeRange(start: string, end: string | null): { start: string; end: string } {
  const rangeEnd = end ?? start;
  if (rangeEnd < start) {
    return { start: rangeEnd, end: start };
  }
  return { start, end: rangeEnd };
}

function dayOffset(windowStart: string, iso: string): number {
  return Math.round((isoToUtcMs(iso) - isoToUtcMs(windowStart)) / 86_400_000);
}

function inclusiveDaySpan(start: string, end: string): number {
  return dayOffset(start, end) + 1;
}

function datePayload(iso: string, end?: string): { t: 'date'; v: string; end?: string } {
  if (end && end !== iso) {
    return { t: 'date', v: iso, end };
  }
  return { t: 'date', v: iso };
}

export function formatTimelineRangeLabel(startIso: string, endIso: string): string {
  return formatDateValue(datePayload(startIso, endIso === startIso ? undefined : endIso), 'short');
}

export function timelineBarPlacements(
  rows: BlockRecord[],
  propertiesByRowId: Record<string, BlockPropertyRecord[]>,
  columnId: string,
  days: TimelineDay[],
  dayWidthPx: number = TIMELINE_DAY_WIDTH_PX,
): { placements: TimelineBarPlacement[]; undated: BlockRecord[] } {
  if (days.length === 0) {
    return { placements: [], undated: rows };
  }

  const windowStart = days[0]!.iso;
  const windowEnd = days[days.length - 1]!.iso;
  const placements: TimelineBarPlacement[] = [];
  const undated: BlockRecord[] = [];

  for (const row of rows) {
    const { start, end } = rowDateRange(row, propertiesByRowId, columnId);
    if (!start) {
      undated.push(row);
      continue;
    }
    const { start: rangeStart, end: rangeEnd } = normalizeRange(start, end);
    if (rangeEnd < windowStart || rangeStart > windowEnd) {
      continue;
    }

    const visibleStart = rangeStart < windowStart ? windowStart : rangeStart;
    const visibleEnd = rangeEnd > windowEnd ? windowEnd : rangeEnd;
    const leftPx = dayOffset(windowStart, visibleStart) * dayWidthPx;
    const widthPx = inclusiveDaySpan(visibleStart, visibleEnd) * dayWidthPx;

    placements.push({
      row,
      startIso: rangeStart,
      endIso: rangeEnd,
      leftPx,
      widthPx,
      extendsBefore: rangeStart < windowStart,
      extendsAfter: rangeEnd > windowEnd,
    });
  }

  placements.sort((a, b) => {
    const byStart = a.startIso.localeCompare(b.startIso);
    if (byStart !== 0) {
      return byStart;
    }
    return (a.row.title ?? '').localeCompare(b.row.title ?? '');
  });

  return { placements, undated };
}

export type TimelineWeekGroup = {
  key: string;
  label: string;
  span: number;
};

export function groupTimelineWeeks(
  days: TimelineDay[],
  labelStyle: 'range' | 'start' = 'range',
): TimelineWeekGroup[] {
  const groups: TimelineWeekGroup[] = [];
  for (let i = 0; i < days.length; i++) {
    const day = days[i]!;
    if (!day.isWeekStart && i > 0) {
      continue;
    }
    let span = 1;
    while (i + span < days.length && !days[i + span]!.isWeekStart) {
      span += 1;
    }
    const start = days[i]!;
    const end = days[i + span - 1]!;
    const startLabel = formatDateValue(datePayload(start.iso), 'short');
    const endLabel = formatDateValue(datePayload(end.iso), 'short');
    groups.push({
      key: start.iso,
      label:
        labelStyle === 'start' || span === 1 ? startLabel : `${startLabel} – ${endLabel}`,
      span,
    });
    i += span - 1;
  }
  return groups;
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

function quarterIndex(month: number): number {
  return Math.floor(month / 3);
}

function dateFromIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

function shortMonth(date: Date): string {
  return date.toLocaleString(undefined, { month: 'short' });
}

export type TimelineBounds = {
  startIso: string;
  endIso: string;
  label: string;
};

/** Inclusive window for the span that contains `month` (0-based). */
export function timelineBounds(year: number, month: number, span: TimelineSpan): TimelineBounds {
  if (span === 'year') {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    return {
      startIso: formatIsoFromDate(start),
      endIso: formatIsoFromDate(end),
      label: String(year),
    };
  }
  if (span === 'quarter') {
    const startMonth = quarterIndex(month) * 3;
    const start = new Date(year, startMonth, 1);
    const end = new Date(year, startMonth + 3, 0);
    return {
      startIso: formatIsoFromDate(start),
      endIso: formatIsoFromDate(end),
      label: `${shortMonth(start)} – ${shortMonth(end)} ${end.getFullYear()}`,
    };
  }
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return {
    startIso: formatIsoFromDate(start),
    endIso: formatIsoFromDate(end),
    label: monthLabel(year, month),
  };
}

export function shiftTimelineAnchor(
  year: number,
  month: number,
  span: TimelineSpan,
  delta: number,
): { year: number; month: number } {
  if (span === 'year') {
    return { year: year + delta, month };
  }
  if (span === 'quarter') {
    const quarterStart = quarterIndex(month) * 3;
    const offset = month - quarterStart;
    const next = shiftMonth(year, quarterStart, delta * 3);
    return { year: next.year, month: next.month + offset };
  }
  return shiftMonth(year, month, delta);
}

function daysFromIsoRange(startIso: string, endIso: string, todayIso: string, focusMonth: number | null): TimelineDay[] {
  const days: TimelineDay[] = [];
  const cursor = dateFromIso(startIso);
  const end = dateFromIso(endIso);
  while (cursor.getTime() <= end.getTime()) {
    const iso = formatIsoFromDate(cursor);
    days.push({
      iso,
      dayOfMonth: cursor.getDate(),
      weekday: (cursor.getDay() + 6) % 7,
      isWeekStart: ((cursor.getDay() + 6) % 7) === 0,
      isToday: iso === todayIso,
      inMonth: focusMonth == null || cursor.getMonth() === focusMonth,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/** Days shown for a span. Month keeps the partial weeks around the month. */
export function buildTimelineSpanDays(
  year: number,
  month: number,
  span: TimelineSpan,
  todayIso: string = todayIsoDate(),
): TimelineDay[] {
  if (span === 'month') {
    return buildTimelineDays(year, month, todayIso);
  }
  const { startIso, endIso } = timelineBounds(year, month, span);
  return daysFromIsoRange(startIso, endIso, todayIso, null);
}

export function groupTimelineByMonth(days: TimelineDay[]): TimelineWeekGroup[] {
  const groups: TimelineWeekGroup[] = [];
  let index = 0;
  while (index < days.length) {
    const key = monthKey(days[index]!.iso);
    let span = 1;
    while (index + span < days.length && monthKey(days[index + span]!.iso) === key) {
      span += 1;
    }
    groups.push({
      key,
      label: shortMonth(dateFromIso(days[index]!.iso)),
      span,
    });
    index += span;
  }
  return groups;
}

export function groupTimelineByQuarter(days: TimelineDay[]): TimelineWeekGroup[] {
  const groups: TimelineWeekGroup[] = [];
  let index = 0;
  while (index < days.length) {
    const start = dateFromIso(days[index]!.iso);
    const quarter = quarterIndex(start.getMonth());
    const year = start.getFullYear();
    let span = 1;
    while (index + span < days.length) {
      const next = dateFromIso(days[index + span]!.iso);
      if (next.getFullYear() !== year || quarterIndex(next.getMonth()) !== quarter) {
        break;
      }
      span += 1;
    }
    const end = dateFromIso(days[index + span - 1]!.iso);
    groups.push({
      key: `${year}-q${quarter + 1}`,
      label: `${shortMonth(start)} – ${shortMonth(end)}`,
      span,
    });
    index += span;
  }
  return groups;
}

function clampDayWidth(fit: number, min: number, max: number): number {
  const clamped = Math.min(max, Math.max(min, fit));
  if (clamped !== fit) {
    return clamped;
  }
  return Math.floor(fit * 100) / 100;
}

/**
 * Day column width. Month stays at a readable day size. Quarter and year
 * shrink to the visible track so the whole period is on screen.
 */
export function timelineDayWidth(span: TimelineSpan, dayCount: number, availablePx: number): number {
  if (span === 'month' || dayCount <= 0) {
    return TIMELINE_DAY_WIDTH_PX;
  }
  if (availablePx <= 0) {
    return span === 'quarter' ? 14 : 3.5;
  }
  const fit = availablePx / dayCount;
  if (span === 'quarter') {
    return clampDayWidth(fit, 4, 22);
  }
  return clampDayWidth(fit, 1, 8);
}

export function timelineStepLabel(span: TimelineSpan): string {
  if (span === 'year') {
    return 'year';
  }
  if (span === 'quarter') {
    return 'quarter';
  }
  return 'month';
}

export { monthLabel, shiftMonth };
